import type { JournalEntry } from "@/apis/gql/types";
import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type UpdateJournalEntryInput = {
  journalEntryId: string;
  bookingText?: string | null;
  documentNumber?: string | null;
};

const UPDATE_JOURNAL_ENTRY_MUTATION = gql`
  mutation UpdateJournalEntry(
    $journalEntryId: String!
    $bookingText: String
    $documentNumber: String
  ) {
    updateJournalEntry(
      journalEntryId: $journalEntryId
      bookingText: $bookingText
      documentNumber: $documentNumber
    ) {
      journalEntryId
      journalId
      tenantId
      timestamp
      status
      documentReference
      documentType
      bookingText
      number
      postingEventId
      createdBy
      createdAt
      updatedAt
      deletedAt
    }
  }
`;

export const useUpdateJournalEntry = () => {
  const { t } = useTranslation();
  const [updateJournalEntry, { data, loading, error }] = useMutation<{
    updateJournalEntry: JournalEntry;
  }>(UPDATE_JOURNAL_ENTRY_MUTATION, {
    refetchQueries: ["GetAllJournalEntries"],
  });

  const handleUpdateJournalEntry = async (
    data: UpdateJournalEntryInput,
    onSuccess: (data: JournalEntry) => void = noop,
    onError: () => void = noop,
  ) => {
    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateJournalEntry({
        variables: {
          journalEntryId: data.journalEntryId,
          bookingText: data.bookingText,
          documentNumber: data.documentNumber,
        },
      });

      if (response.errors) {
        throw new Error("error updating journal entry");
      }

      if (!response.data?.updateJournalEntry) {
        throw new Error("No data returned from updateJournalEntry");
      }

      onSuccess(response.data.updateJournalEntry);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdateJournalEntry, { data, loading, error }] as const;
};
